import { Slider, TextField } from '@frameio/vapor';
import { useLocalOptimism } from '@frameio/vapor-migration/hooks';
import { SliderContainer } from '../../sharedStyles';

type RotationControlProps = {
  isDisabled: boolean;
  value: number;
  onChange: (value: number) => void;
};

// RotationControl: Allows users to adjust rotation value using a slider and input field
export const RotationControl: React.FC<RotationControlProps> = ({
  isDisabled,
  value,
  onChange,
}) => {
  const [inputValue, setInputValue] = useLocalOptimism(value.toString());

  // Handle slider value changes
  const handleSliderChange = (newValue: number) => {
    const roundedValue = Math.round(newValue);
    onChange(newValue);
    setInputValue(roundedValue.toString());
  };

  // Handle manual input changes
  let negativeMultiplier = 1;
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = Math.round(
      parseFloat(e.target.value.replace(/[^0-9.]/g, ''))
    );
    if (e.target.value.includes('-')) {
      negativeMultiplier = -1;
    }
    const multipliedValue = negativeMultiplier * newValue;
    setInputValue(
      `${isNaN(multipliedValue) ? '0' : multipliedValue.toString()}`
    );
  };

  // Validate and update value when input loses focus
  const handleInputBlur = () => {
    let newValue = parseInt(inputValue, 10);
    if (isNaN(newValue) || newValue < -45) {
      newValue = -45;
    } else if (newValue > 45) {
      newValue = 45;
    }
    onChange(newValue);
    setInputValue(newValue.toString());
  };

  // Handle Enter key press in input field
  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleInputBlur();
    }
  };

  return (
    <SliderContainer>
      <Slider
        disabled={isDisabled}
        max={45}
        min={-45}
        onChange={(v) => handleSliderChange(v)}
        showTooltip={(v) => `${v.toFixed(0)}°`}
        style={{ flex: 1 }}
        value={isDisabled ? 0 : value}
      />
      <TextField
        onBlur={handleInputBlur}
        onChange={handleInputChange}
        onKeyDown={handleInputKeyDown}
        style={{ maxWidth: '50px' }}
        type="text"
        value={isDisabled ? `0°` : `${Math.round(parseFloat(inputValue))}°`}
      />
    </SliderContainer>
  );
};
